/* istanbul ignore file */
// eslint-disable-next-line simple-import-sort/imports
import 'src/scripts/wdyr';
import React from 'react';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import { CookieModal } from '@noths/polaris-client-gdpr-compliance';
import { GTMWithDataLayer } from '@noths/polaris-client-google-analytics';
import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { BaseStyles } from '@noths/polaris-client-ribbons-base';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { getLayout, LayoutTypes, ErrorBoundary } from '@noths/polaris-client-next-components';
import { SkipLinks, useSmartAppBanner } from '@noths/polaris-client-site-chrome-ui';
import type { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import type { AppInitialProps, AppProps } from 'next/app';
import NextApp from 'next/app';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import Head from 'next/head';

import type { NextConfig } from 'src/types/NextConfig';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { isProd, isProdLike } from 'src/environment';
import { getServerSidePageEventObject } from 'src/utils/gaPageEvent';

const BrowserRum = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_rum" */ '@noths/polaris-client-next-components/src/monitoring/BrowserRum'
  ).then((mod) => mod.BrowserRum),
);

const BrowserLogs = dynamic(() =>
  import(
    /* webpackChunkName: "datadog_browser_logs" */ '@noths/polaris-client-next-components/src/monitoring/BrowserLogs'
  ).then((mod) => mod.BrowserLogs),
);

const config = getConfig();
const { appVersion, version } = config.publicRuntimeConfig;

interface CustomAppProps extends AppProps {
  featureFlags?: FeatureFlags;
  layoutType?: LayoutTypes;
  requestIsFromBot?: boolean;
  searchedTerm?: string;
  siteChromeCompactMode?: boolean;
  siteChromeContent?: SiteChromeContent | null;
  stickyTopBanner?: boolean;
}

const App = (props: CustomAppProps) => {
  useSmartAppBanner();
  const pageEventData = getServerSidePageEventObject(
    version,
    appVersion,
    props.pageProps?.pageEventCategory,
    props.requestIsFromBot,
  );

  const {
    publicRuntimeConfig: { ENABLE_LOGGING, NODE_ENV, env, monitoring },
  } = config as NextConfig;

  const errorBoundaryProps = {
    env: NODE_ENV,
    isLoggingEnabled: ENABLE_LOGGING,
  };

  const defaultLayoutType = LayoutTypes.CLIENT_SITE_CHROME_UI;
  const siteChromeContent = props.pageProps?.siteChromeContent ?? props.siteChromeContent;
  const stickyTopBanner = props.pageProps?.stickyTopBanner ?? props.stickyTopBanner;
  const layoutType = props.pageProps?.layoutType ?? props.layoutType ?? defaultLayoutType;
  const LayoutComponent = getLayout(layoutType);
  const renderMonitoring = monitoring?.applicationId && env === 'production';

  return (
    <>
      <Head>
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <CookieModal useTestScript={!isProd()} />
      </Head>
      {renderMonitoring && (
        <>
          <BrowserRum
            applicationId={monitoring?.applicationId}
            clientToken={monitoring?.clientToken}
            featureFlags={props.featureFlags}
            monitoringEnv={monitoring?.monitoringEnv}
            rumSampleRate={monitoring?.rumSampleRate}
            rumTracingURLs={monitoring?.rumTracingURLs}
            serviceName={monitoring?.serviceName}
            version={version}
          />
          <BrowserLogs
            clientToken={monitoring?.clientToken}
            forwardConsoleLogs={monitoring.forwardConsoleLogs}
            logsLoggerLevel={monitoring?.logsLoggerLevel}
            logsSampleRate={monitoring?.logsSampleRate}
            monitoringEnv={monitoring?.monitoringEnv}
            serviceName={monitoring?.serviceName}
            version={version}
          />
        </>
      )}
      <BaseStyles />
      <GTMWithDataLayer pageEventData={pageEventData as Record<string, unknown>} />
      <SkipLinks links={[{ content: 'Skip to content', sectionId: SKIP_CONTENT_TARGET_ID }]} />
      <LayoutComponent
        NODE_ENV={NODE_ENV}
        featureFlags={props.featureFlags}
        isProdLikeEnv={isProdLike()}
        mergeBasket={props.pageProps?.mergeBasket}
        searchedTerm={props.searchedTerm}
        siteChromeCompactMode={props.siteChromeCompactMode ?? false}
        siteChromeContent={siteChromeContent}
        stickyTopBanner={stickyTopBanner}
      >
        <ErrorBoundary {...errorBoundaryProps}>
          <props.Component {...props.pageProps} />
        </ErrorBoundary>
      </LayoutComponent>
    </>
  );
};

App.getInitialProps = async (
  appContext: AppContextPlusCookies,
): Promise<AppInitialProps & Partial<CustomAppProps>> => {
  const {
    ctx: { req, res },
    router,
  } = appContext;

  if (req && res) {
    setLocaleCookies({ req, res });
  }

  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const appProps = await NextApp.getInitialProps(appContext);
  const requestIsFromBot = (req && getMvtStrategyFromRequest(req) === 'bot') || false;

  if (router.route === '/404') {
    const [siteChromeContent] = await fetchGlobalContent({ featureFlags });

    return {
      ...appProps,
      siteChromeContent,
      featureFlags,
      requestIsFromBot,
    };
  }

  return { ...appProps, featureFlags, requestIsFromBot };
};

export default App;
